import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/* ==========================================================================
   Tiptap-backed rich text editor used by every `tier: 'rich'` block.
   --------------------------------------------------------------------------
   - Bold / italic / underline / strike-through
   - Bullet + numbered lists
   - Image insertion with `data-layout` so the renderer knows whether the
     image flows inline, sits as a block, or floats left/right of the text
   - Inline math via KaTeX. We render into a `<span class="math">` carrying
     the source TeX in `data-tex` so we can re-edit later.
   ========================================================================== */

interface Props {
  html: string;
  onChange: (html: string) => void;
  /** Pass a stable id so we know when to remount the editor. */
  blockId: string;
  inline?: boolean;
}

function ToolbarButton({
  label, active, onClick, title,
}: { label: string; active?: boolean; onClick: () => void; title?: string }) {
  return (
    <button
      type="button"
      className={`re-btn ${active ? 'is-active' : ''}`}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title || label}
    >
      {label}
    </button>
  );
}

export function RichEditor({ html, onChange, blockId, inline }: Props) {
  const lastHtmlRef = useRef(html);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        codeBlock: false,
      }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-layout': {
              default: 'block',
              parseHTML: (el) => el.getAttribute('data-layout') || 'block',
              renderHTML: (attrs) => ({ 'data-layout': attrs['data-layout'] }),
            },
            width: {
              default: null,
              parseHTML: (el) => el.getAttribute('width'),
              renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
            },
          };
        },
      }).configure({ inline: false, allowBase64: true }),
    ],
    content: html || '<p></p>',
    editorProps: {
      attributes: {
        class: `re-content ${inline ? 'is-inline' : ''}`,
      },
    },
    onUpdate({ editor: ed }) {
      const next = ed.getHTML();
      lastHtmlRef.current = next;
      onChange(next);
    },
  }, [blockId]);

  /* If the props change externally (e.g. user undoes a parent-level change),
     keep the editor in sync without thrashing on every keystroke. */
  useEffect(() => {
    if (!editor) return;
    if (html !== lastHtmlRef.current) {
      editor.commands.setContent(html || '<p></p>', { emitUpdate: false });
      lastHtmlRef.current = html;
    }
  }, [html, editor]);

  if (!editor) return null;

  const insertImage = () => {
    const url = window.prompt('Image URL');
    if (!url) return;
    const layout = window.prompt('Layout: block | inline | float-left | float-right', 'block') || 'block';
    const width = window.prompt('Width (px, blank for natural)', '320') || '';
    editor
      .chain()
      .focus()
      .setImage({ src: url, alt: '', ...(width ? { width } : {}) } as never)
      .updateAttributes('image', { 'data-layout': layout })
      .run();
  };

  const insertMath = () => {
    const tex = window.prompt('Inline math (LaTeX), e.g. x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}', '');
    if (!tex) return;
    let svg: string;
    try {
      svg = katex.renderToString(tex, { throwOnError: false, output: 'mathml' });
    } catch {
      svg = `<code>${tex}</code>`;
    }
    editor.chain().focus()
      .insertContent(`<span class="math" data-tex="${tex.replace(/"/g, '&quot;')}">${svg}</span>`)
      .run();
  };

  return (
    <div className="re-wrap">
      <BubbleMenu editor={editor}>
        <div className="re-toolbar">
          <ToolbarButton label="B" active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (⌘B)" />
          <ToolbarButton label="I" active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (⌘I)" />
          <ToolbarButton label="U" active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline" />
          <ToolbarButton label="S" active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough" />
          <span className="re-sep" />
          <ToolbarButton label="• List" active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list" />
          <ToolbarButton label="1. List" active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list" />
          <span className="re-sep" />
          <ToolbarButton label="Img" onClick={insertImage} title="Insert image" />
          <ToolbarButton label="∑" onClick={insertMath} title="Insert math" />
          <span className="re-sep" />
          <ToolbarButton label="Clear" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Strip formatting" />
        </div>
      </BubbleMenu>
      <EditorContent editor={editor} />
    </div>
  );
}
